import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, X } from "lucide-react";
import { ASSETS } from "../lib/constants";

export default function GalleryPage() {
  const { t } = useTranslation();
  const [lightbox, setLightbox] = useState(null);
  const photos = ASSETS.gallery;

  return (
    <div data-testid="gallery-page" className="bg-brand-black">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10 bg-brand-black py-20 md:py-28">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 right-0 h-[420px] w-[700px] rounded-full bg-brand-orange/20 blur-[140px]"
        />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-brand-orange">
            <Camera className="h-3.5 w-3.5" />
            {t("gallery.eyebrow", "Real riders · Real adventures")}
          </span>
          <h1 className="mt-3 font-display text-5xl uppercase leading-none text-white sm:text-6xl lg:text-7xl">
            {t("gallery.title", "The Cascante Gallery")}
          </h1>
          <p className="mt-4 max-w-2xl text-base text-white/70">
            {t(
              "gallery.subtitle",
              "Snapshots from our customers exploring Nicaragua on two wheels."
            )}
          </p>
          <p className="mt-2 text-xs uppercase tracking-[0.22em] text-white/40">
            {photos.length} {t("gallery.photos", "photos")} · San Juan del Sur, NIC
          </p>
        </div>
      </section>

      {/* Masonry-style grid */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-10 lg:py-16">
        <div className="columns-2 gap-3 sm:gap-4 md:columns-3 lg:columns-4">
          {photos.map((src, i) => (
            <motion.button
              key={`${src}-${i}`}
              type="button"
              data-testid={`gallery-page-photo-${i}`}
              onClick={() => setLightbox(src)}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.45, delay: (i % 8) * 0.04, ease: "easeOut" }}
              className="group relative mb-3 block w-full overflow-hidden border-2 border-brand-orange/20 transition hover:border-brand-orange sm:mb-4"
            >
              <img
                src={src}
                alt={`Cascante Moto rider ${i + 1}`}
                loading="lazy"
                className="block h-auto w-full object-cover transition duration-700 group-hover:scale-[1.04]"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 transition group-hover:opacity-100" />
              <div className="pointer-events-none absolute bottom-3 left-3 text-[10px] font-bold uppercase tracking-[0.22em] text-white opacity-0 transition group-hover:opacity-100">
                #{i + 1} · SJDS
              </div>
            </motion.button>
          ))}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
          >
            <button
              type="button"
              data-testid="gallery-lightbox-close"
              onClick={() => setLightbox(null)}
              aria-label="Close"
              className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center border border-white/30 bg-black/50 text-white hover:bg-brand-orange hover:border-brand-orange"
            >
              <X className="h-5 w-5" />
            </button>
            <motion.img
              key={lightbox}
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.25 }}
              src={lightbox}
              alt="Cascante Moto rider"
              className="max-h-[90vh] max-w-[95vw] object-contain border-2 border-brand-orange"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
